/**
 * ContinueAsGuestButton — soft entry that starts a 24h guest session and
 * routes the visitor straight into the assessment.
 *
 * Calls POST /api/guest/session/start with a Turnstile token. The widget is
 * rendered explicitly in **Managed mode** (size=flexible, appearance=execute,
 * execution=execute) so Cloudflare stays silent for low-risk visitors and
 * only renders the checkbox challenge when its risk model demands it.
 *
 * Saves the returned guest token in localStorage under `fg_guest_token`. The
 * api client picks it up automatically and attaches `X-Guest-Token` on
 * guest-scoped requests (assessments etc).
 *
 * GA4 events: `guest_started` on click, `guest_completed` when assessment is
 * submitted under the guest session (handled in QuestionnairePage).
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { trackEvent } from "../lib/tracking";
import { toast } from "sonner";
import { Loader2, UserCircle } from "lucide-react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const GUEST_TOKEN_KEY = "fg_guest_token";
const GUEST_SESSION_KEY = "fg_guest_session_id";
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const MOUNT_ID = "cf-turnstile-mount";


// Render Turnstile widget once on mount; expose widgetId via a module-level
// ref. Calling execute(widgetId) on click then drives the registered callback
// to resolve the next token via a one-shot promise.
let _widgetId = null;
let _renderInFlight = null;
let _pendingResolver = null;

function ensureTurnstileScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("cf-turnstile-script")) return;
  const s = document.createElement("script");
  s.id = "cf-turnstile-script";
  s.src = TURNSTILE_SCRIPT;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}


async function waitForTurnstileScript(maxMs = 8000) {
  if (typeof window === "undefined") return false;
  const start = Date.now();
  while (!window.turnstile) {
    if (Date.now() - start > maxMs) return false;
    await new Promise((r) => setTimeout(r, 80));
  }
  return true;
}


async function renderWidgetOnce() {
  if (_widgetId != null) return _widgetId;
  // Coalesce concurrent calls (StrictMode double-mount, re-render races, etc.)
  // so we only ever call window.turnstile.render() once per page.
  if (_renderInFlight) return _renderInFlight;
  _renderInFlight = (async () => {
    const ok = await waitForTurnstileScript();
    if (!ok) return null;
    let mount = document.getElementById(MOUNT_ID);
    const mountStart = Date.now();
    while (!mount && Date.now() - mountStart < 2000) {
      await new Promise((r) => setTimeout(r, 50));
      mount = document.getElementById(MOUNT_ID);
    }
    if (!mount) return null;
    try {
      // Managed-mode compatible config: stays silent for low-risk visitors
      // and surfaces a checkbox challenge only when Cloudflare's risk model
      // demands it. Works regardless of whether the dashboard widget mode is
      // Managed, Non-interactive, or Invisible (size=flexible is accepted by
      // all three visible modes; Invisible-only widgets must use
      // size=invisible — switch the CF dashboard widget mode to Managed for
      // the recommended setup).
      _widgetId = window.turnstile.render("#" + MOUNT_ID, {
        sitekey: SITE_KEY,
        size: "flexible",
        appearance: "execute",
        execution: "execute",
        retry: "auto",
        "refresh-expired": "auto",
        callback: (token) => {
          if (_pendingResolver) { _pendingResolver(token); _pendingResolver = null; }
        },
        "error-callback": (code) => {
          // eslint-disable-next-line no-console
          console.warn("[turnstile] error", code);
          if (_pendingResolver) { _pendingResolver(null); _pendingResolver = null; }
        },
        "expired-callback": () => {
          try { window.turnstile.reset(_widgetId); } catch { /* ignore */ }
        },
      });
      return _widgetId;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[turnstile] render failed", e);
      return null;
    }
  })();
  const id = await _renderInFlight;
  _renderInFlight = null;
  return id;
}


async function getTurnstileToken() {
  if (typeof window === "undefined") return null;
  if (!SITE_KEY) return "1x00000000000000000000AA"; // dev bypass when key absent
  const id = await renderWidgetOnce();
  if (id == null) return null;
  return new Promise((resolve) => {
    _pendingResolver = resolve;
    try {
      // Reset to drop any expired/used token, then re-execute for a fresh one.
      window.turnstile.reset(id);
      window.turnstile.execute(id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[turnstile] execute failed", e);
      _pendingResolver = null;
      resolve(null);
    }
    // Safety timeout — never hang forever.
    setTimeout(() => {
      if (_pendingResolver === resolve) { _pendingResolver = null; resolve(null); }
    }, 15000);
  });
}


export default function ContinueAsGuestButton({ destination = "/questionnaire" }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (SITE_KEY) {
      ensureTurnstileScript();
      // Pre-warm: render the widget as soon as the script is parsed so the
      // user's click only has to call execute() — eliminates the cold-start
      // race that was producing the "Turnstile token missing" 403.
      renderWidgetOnce();
    }
  }, []);

  async function start() {
    setBusy(true);
    try {
      trackEvent("guest_started", { location: window.location.pathname });
      const token = await getTurnstileToken();
      if (!token) {
        throw new Error("Verification didn't complete. Please try again.");
      }
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/guest/session/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Turnstile-Token": token,
        },
        body: JSON.stringify({ fingerprint: navigator.userAgent.slice(0, 80) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Could not start guest session");
      }
      const data = await r.json();
      localStorage.setItem(GUEST_TOKEN_KEY, data.guest_token);
      localStorage.setItem(GUEST_SESSION_KEY, data.guest_session_id);
      navigate(destination);
    } catch (e) {
      toast.error(e.message || "Couldn't start your guest session. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={start}
        disabled={busy}
        className="w-full text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50 border border-emerald-200 bg-white"
        data-testid="continue-as-guest-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCircle className="w-4 h-4 mr-2" />}
        Continue as Guest
      </Button>
      {/* Turnstile mount — Managed mode + appearance=execute keeps this
          empty/zero-height for low-risk visitors. A checkbox challenge
          renders here only when Cloudflare's risk model demands it. */}
      {SITE_KEY ? (
        <div
          id={MOUNT_ID}
          className="mt-2 flex justify-center min-h-0 [&:empty]:hidden"
          data-testid="turnstile-mount"
        />
      ) : null}
      <p className="text-[11px] text-emerald-900/55 text-center mt-1">
        Try the assessment first · no account needed
      </p>
    </>
  );
}
